import profileActions from "actions/profile";
import CheckGreen from "check-green.svg";
import AtiSuAuthButton from "components/auth/atiSuAuthButton";
import Loader from "components/shared/loader";
import { paths } from "helpers/paths";
import { ymEvent } from "helpers/system";
import { type CrmUtmData, clearRegistrationAttemptId, getCrmUtmData, getRegistrationAttemptId } from "helpers/tracking";
import { useRouter } from "hooks/useRouter";
import { UmamiManager } from "lib/umami";
import moment from "moment";
import Person from "person.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Action, ActionFunctionAny } from "redux-actions";
import { register } from "sources/account";
import { type ProductMetricsEventName, productMetricsEventNames, sendEventLog } from "sources/eventLogs";
import "react-multi-carousel/lib/styles.css";
import { CustomButtonGroup, NullArrow } from "components/shared/carouselControls";
import { Step1CompanyInfo, validateEmail, validateInn } from "./step1CompanyInfo";
import { Step2UserInfo, validatePhoneNumber } from "./step2UserInfo";

const responsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 3 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 3 },
};

const getRegistrationErrorMessage = (err: unknown) =>
    err instanceof Error ? err.message : "Registration request failed";

export function Registration(props: MapDispatchToPropsType) {
    const router = useRouter();
    const crmUtmDataRef = useRef<CrmUtmData>({});
    const registrationAttemptIdRef = useRef("");

    const [state, setState] = useState({
        name: "",
        email: "",
        phoneNumber: "",
        inn: "",
        kpp: "",
        password: "",
        confirmPassword: "",
    });

    const [step, setStep] = useState(1);

    const [selectValue, setSelectValue] = useState<string | undefined>();

    const [showPassword, setShowPassword] = useState(false);
    const [manual, setManual] = useState(false);

    const [loading, setLoading] = useState(false);
    const [licenseAgreementAccepted, setLicenseAgreementAccepted] = useState(false);
    const [personalDataAccepted, setPersonalDataAccepted] = useState(false);

    const isStep1Valid =
        (state.name.length > 0 || state.inn.length > 0) &&
        validateEmail(state.email) &&
        (manual ? validateInn(state.inn) : true);
    const isStep2Valid = validatePhoneNumber(state.phoneNumber) && state.password.length > 0;
    const registrationConsentsAccepted = licenseAgreementAccepted && personalDataAccepted;
    const canStartRegistration = isStep1Valid && registrationConsentsAccepted;
    const canSubmitRegistration = isStep2Valid && registrationConsentsAccepted;

    const getCurrentCrmUtmData = useCallback(async () => {
        const crmUtmData = await getCrmUtmData();
        crmUtmDataRef.current = crmUtmData;

        return crmUtmData;
    }, []);

    const getCurrentRegistrationAttemptId = useCallback(() => {
        const registrationAttemptId = getRegistrationAttemptId();
        registrationAttemptIdRef.current = registrationAttemptId;

        return registrationAttemptId;
    }, []);

    const sendRegistrationMetric = useCallback(
        (
            name: ProductMetricsEventName,
            payload: Record<string, unknown> = {},
            options: { isSuccess?: boolean; errorMessage?: string } = {},
            crmUtmData = crmUtmDataRef.current,
        ) =>
            sendEventLog({
                name,
                ...options,
                metricClientId: crmUtmData.clientId,
                registrationAttemptId: registrationAttemptIdRef.current || getCurrentRegistrationAttemptId(),
                payload: {
                    ...payload,
                    utmData: crmUtmData,
                },
            }),
        [getCurrentRegistrationAttemptId],
    );

    useEffect(() => {
        document.body.classList.add("registration-body-scroll");
        getCurrentRegistrationAttemptId();

        getCurrentCrmUtmData().then((crmUtmData) =>
            sendRegistrationMetric(productMetricsEventNames.RegistrationOpened, {}, {}, crmUtmData),
        );

        return () => document.body.classList.remove("registration-body-scroll");
    }, [getCurrentCrmUtmData, getCurrentRegistrationAttemptId, sendRegistrationMetric]);

    const registerHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!canSubmitRegistration) {
            return;
        }

        ymEvent("ButtonCreateAnAccountClicked");

        setLoading(true);

        let res: any;
        let crmUtmData = crmUtmDataRef.current;

        try {
            crmUtmData = await getCurrentCrmUtmData();
            res = await register(
                {
                    ...state,
                    confirmPassword: state.password,
                    utmData: crmUtmData,
                    kpp: state.kpp.length > 0 ? state.kpp : null,
                },
                {
                    productMetricHeaders: {
                        metricClientId: crmUtmData.clientId,
                        registrationAttemptId: getCurrentRegistrationAttemptId(),
                    },
                    returnErrorText: true,
                    throwOnError: true,
                },
            );
        } catch (err) {
            console.error({ type: "error", message: "Неудачный запрос", title: "Ошибка.", err });
            sendRegistrationMetric(
                productMetricsEventNames.RegistrationPhonePasswordEntered,
                { phoneNumber: state.phoneNumber },
                { isSuccess: false, errorMessage: getRegistrationErrorMessage(err) },
                crmUtmData,
            );
            return;
        } finally {
            setLoading(false);
        }

        if (!(res && res.tokens)) {
            sendRegistrationMetric(
                productMetricsEventNames.RegistrationPhonePasswordEntered,
                { phoneNumber: state.phoneNumber },
                { isSuccess: false, errorMessage: res?.errorText || "Registration request failed" },
                crmUtmData,
            );
            return;
        }

        if (res && res.tokens) {
            const { accessToken, refreshToken, currentUser } = res.tokens;
            if (accessToken) {
                const { token, expiresIn } = accessToken;
                localStorage.setItem("token", token);
                localStorage.setItem("refreshToken", refreshToken);
                localStorage.setItem("tokenExpiresIn", moment().add(expiresIn, "seconds").toISOString());
                localStorage.setItem("currentUser", JSON.stringify(currentUser));

                UmamiManager.track("CreateAccountButtonClicked", {
                    CompanyID: currentUser?.organizationId,
                    CompanyName: currentUser?.organizationName,
                    Name: currentUser?.fullName,
                    Roles: currentUser?.roles.map((roleName: string) => roleName).join(", "),
                    CompanyType: "CR Start",
                });

                sendRegistrationMetric(
                    productMetricsEventNames.RegistrationPhonePasswordEntered,
                    {
                        phoneNumber: state.phoneNumber,
                        userId: currentUser?.id,
                        organizationId: currentUser?.organizationId,
                        organizationName: currentUser?.organizationName,
                    },
                    {},
                    crmUtmData,
                ).then((isSent) => {
                    if (isSent) {
                        clearRegistrationAttemptId();
                    }
                });

                props.getInitialProfile({ token, currentUser });
                router.push(paths.registrationCreateRelay);
            }
        }
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleSelect = (data: { value: string; option: { name: string; inn: string; kpp: string } }) => {
        if (data.option) {
            setSelectValue(data.value);
            setState({ ...state, name: data.option.name, inn: data.option.inn });
        } else {
            setSelectValue(undefined);
            setState({ ...state, name: "", inn: "" });
        }
    };

    const handleNextStep = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (canStartRegistration) {
            getCurrentCrmUtmData().then((crmUtmData) =>
                sendRegistrationMetric(
                    productMetricsEventNames.RegistrationInnEmailEntered,
                    {
                        name: state.name,
                        inn: state.inn,
                        kpp: state.kpp,
                        email: state.email,
                        ...(state.phoneNumber ? { phoneNumber: state.phoneNumber } : {}),
                    },
                    {},
                    crmUtmData,
                ),
            );
            UmamiManager.track("FreeStartButtonClicked", {
                CompanyName: state.name,
                CompanyType: "CR Start",
            });
            setStep(2);
            ymEvent("RegistrationStep2Visited");
        }
    };

    const handlePrevStep = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStep(1);
    };

    return (
        <div className="registration-page no-overflow h-100">
            <div className="identity-container">
                {loading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <div className="registration-container__shadow-top" />
                <div className="registration-container__shadow" />
                <div className="registration-container__background" />
                <div className="registration-container__header">
                    <div className="registration-container__header--logo" />
                    <div className="registration-container__header--sign-in">
                        <Link to={paths.login}>
                            <Person /> Вход
                        </Link>
                    </div>
                </div>
                <div className="registration-panel__wrap">
                    <div className="registration-panel__title">
                        {step === 1 ? (
                            "Привет! Начнём с простого"
                        ) : (
                            <>
                                Почти готово!&nbsp;
                                <span className="font-size-52">
                                    <CheckGreen />
                                </span>
                            </>
                        )}
                    </div>
                    <div className="registration-panel__subtitle">
                        {step === 1 ? (
                            <>
                                Создайте аккаунт и тестируйте платформу
                                <br /> 14 дней бесплатно
                            </>
                        ) : (
                            <>
                                Осталось добавить телефон и придумать пароль
                                <br />
                                для безопасного входа
                            </>
                        )}
                    </div>
                    <div className="registration-panel__container">
                        <div className="registration-panel">
                            <form className="d-flex flex-column w-100" autoComplete="off" onSubmit={registerHandler}>
                                <div className="registration-panel__inner--title">Одна минута до старта</div>
                                <div className="registration-panel__fields">
                                    <div className="registration-panel__inner">
                                        {step === 1 && (
                                            <>
                                                <Step1CompanyInfo
                                                    state={state}
                                                    setState={setState}
                                                    manual={manual}
                                                    setManual={setManual}
                                                    selectValue={selectValue}
                                                    handleSelect={handleSelect}
                                                />
                                                <div className="registration-consents">
                                                    <label
                                                        className="registration-consent"
                                                        htmlFor="licenseAgreementAccepted"
                                                    >
                                                        <input
                                                            className="registration-consent__input"
                                                            type="checkbox"
                                                            id="licenseAgreementAccepted"
                                                            name="licenseAgreementAccepted"
                                                            checked={licenseAgreementAccepted}
                                                            onChange={(e) =>
                                                                setLicenseAgreementAccepted(e.target.checked)
                                                            }
                                                        />
                                                        <span className="registration-consent__box" />
                                                        <span className="registration-consent__text">
                                                            принимаю условия{" "}
                                                            <a
                                                                href="https://cargorun.ru/cr-start-license-agreement"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                соглашения на использование сервисов CARGO.RUN
                                                            </a>
                                                        </span>
                                                    </label>
                                                    <label
                                                        className="registration-consent"
                                                        htmlFor="personalDataAccepted"
                                                    >
                                                        <input
                                                            className="registration-consent__input"
                                                            type="checkbox"
                                                            id="personalDataAccepted"
                                                            name="personalDataAccepted"
                                                            checked={personalDataAccepted}
                                                            onChange={(e) => setPersonalDataAccepted(e.target.checked)}
                                                        />
                                                        <span className="registration-consent__box" />
                                                        <span className="registration-consent__text">
                                                            Я даю согласие ООО «Цифровизация транспорта» на обработку
                                                            моих персональных данных в соответствии с{" "}
                                                            <a
                                                                href="https://cargorun.ru/personal-data-consent"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                Согласием на обработку персональных данных
                                                            </a>{" "}
                                                            и подтверждаю, что ознакомлен с{" "}
                                                            <a
                                                                href="https://cargorun.ru/privacy"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                Политикой обработки персональных данных
                                                            </a>
                                                            .
                                                        </span>
                                                    </label>
                                                </div>
                                            </>
                                        )}
                                        {step === 2 && (
                                            <Step2UserInfo
                                                state={state}
                                                setState={setState}
                                                showPassword={showPassword}
                                                toggleShowPassword={toggleShowPassword}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 mb-2 text-center d-flex justify-content-center gap-2 registration-panel__inner">
                                    {step === 2 && (
                                        <button
                                            className="r-form__button r-form__button--outline"
                                            type="button"
                                            onClick={handlePrevStep}
                                        >
                                            Назад
                                        </button>
                                    )}
                                    {step === 1 ? (
                                        <button
                                            className="r-form__button"
                                            type="button"
                                            onClick={handleNextStep}
                                            disabled={!canStartRegistration}
                                        >
                                            Начать бесплатно
                                        </button>
                                    ) : (
                                        <button
                                            className="r-form__button"
                                            type="submit"
                                            disabled={!canSubmitRegistration}
                                        >
                                            Начать бесплатно
                                        </button>
                                    )}
                                </div>
                                {step === 1 && (
                                    <div className="registration-panel__inner text-center">
                                        <div className="text-muted font-size-12 mb-2">или</div>
                                        <AtiSuAuthButton
                                            className="identity-form__button--content"
                                            metricName="RegistrationAtiSuButtonClicked"
                                        >
                                            Начать через ATI.SU
                                        </AtiSuAuthButton>
                                    </div>
                                )}
                                <div className="registration-panel__inner--text pt-2">
                                    Можно отменить в любой момент
                                </div>
                            </form>
                        </div>
                    </div>
                    {step > 1 ? (
                        <div className="r-steps">
                            <div className="r-steps__item r-steps__item--active">
                                <div className="r-steps__item-circle">1</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                            <div className="r-steps__line r-steps__line--active" />
                            <div className="r-steps__item r-steps__item--active">
                                <div className="r-steps__item-circle">2</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                            <div className="r-steps__line" />
                            <div className="r-steps__item">
                                <div className="r-steps__item-circle">3</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                        </div>
                    ) : (
                        <div className="r-clients r-clients--registration">
                            Нам доверяют транспортные компании по всей России
                            <div className="r-clients__wrap r-relay__certificates mt-3">
                                <Carousel
                                    responsive={responsive}
                                    infinite
                                    autoPlay
                                    autoPlaySpeed={3000}
                                    renderButtonGroupOutside
                                    customLeftArrow={<NullArrow />}
                                    customRightArrow={<NullArrow />}
                                    customButtonGroup={<CustomButtonGroup />}
                                >
                                    {[
                                        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
                                        33, 34,
                                    ].map((n) => (
                                        <div key={n} className="text-center px-1">
                                            <img
                                                className="r-clients__item"
                                                src={`/images/client-logos/logo-${n}.jpg`}
                                                alt=""
                                            />
                                        </div>
                                    ))}
                                </Carousel>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const mapDispatchToProps = (dispatch: ActionFunctionAny<Action<Object>>) => ({
    getInitialProfile: (params: {}) => dispatch(profileActions.getInitialProfile(params)),
});

type MapDispatchToPropsType = ReturnType<typeof mapDispatchToProps>;

export default connect<{}, MapDispatchToPropsType>(() => ({}), mapDispatchToProps)(Registration);
